import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';

import { warehouseService } from '../../services/warehouseService';
import { INPUT_SANITIZERS } from '../../utils/validationHelpers';

export interface ZonePlacement {
  zoneId: number;
  pallets: number;
}

interface StorageReassignmentPickerProps {
  warehouseId: number;
  requiredPallets: number;
  value: ZonePlacement[];
  onChange: (placements: ZonePlacement[]) => void;
  // Reassign damaged-goods (LBD) storage instead of good-stock storage —
  // flips the zone filter below to LBD-only zones instead of excluding them.
  lbdOnly?: boolean;
}

// An LBD (damaged-goods) zone is identified by a zone type of 'lbd' or a code
// starting with 'LBD' — same rule GrnCreate.tsx uses.
const isLbdZone = (zone: any): boolean => {
  const type = String(zone?.zoneType || '').toLowerCase();
  const code = String(zone?.code || '').toLowerCase();
  return type === 'lbd' || code.startsWith('lbd');
};

export default function StorageReassignmentPicker({
  warehouseId,
  requiredPallets,
  value,
  onChange,
  lbdOnly = false,
}: StorageReassignmentPickerProps) {
  const { data: zonesData, isLoading } = useQuery({
    queryKey: ['zones', warehouseId],
    queryFn: () => warehouseService.getZones(warehouseId),
    enabled: !!warehouseId,
  });

  const zones = useMemo(() => {
    if (!zonesData) return [];
    if (zonesData.data && Array.isArray(zonesData.data.items)) return zonesData.data.items;
    if (Array.isArray(zonesData.items)) return zonesData.items;
    return [];
  }, [zonesData]);

  const normalZones = useMemo(
    () => zones.filter((z: any) => (lbdOnly ? isLbdZone(z) : !isLbdZone(z))),
    [zones, lbdOnly],
  );

  const totalPallets = value.reduce((sum, p) => sum + (p.pallets || 0), 0);
  const isBalanced = totalPallets === requiredPallets;

  const updateRow = (index: number, patch: Partial<ZonePlacement>) => {
    const next = value.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const addRow = () => {
    onChange([...value, { zoneId: normalZones[0]?.id ?? 0, pallets: 0 }]);
  };

  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50/50 p-3 space-y-2">
      {value.map((row, index) => (
        <div key={index} className="flex items-center gap-2">
          <select
            value={row.zoneId}
            onChange={(e) => updateRow(index, { zoneId: Number(e.target.value) })}
            disabled={isLoading}
            className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            {normalZones.map((z: any) => (
              <option key={z.id} value={z.id}>
                {z.code}
              </option>
            ))}
          </select>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={row.pallets}
            onChange={(e) => {
              const digits = INPUT_SANITIZERS.digitsOnly(e.target.value);
              updateRow(index, { pallets: digits === '' ? 0 : parseInt(digits, 10) });
            }}
            className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Pallets"
          />
          <button
            onClick={() => removeRow(index)}
            className="p-1.5 text-gray-400 hover:text-red-600"
            title="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}

      <div className="flex items-center justify-between">
        <button
          onClick={addRow}
          disabled={isLoading || normalZones.length === 0}
          className="flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-800 disabled:opacity-50"
        >
          <Plus className="w-3.5 h-3.5" /> Add zone
        </button>
        <span className={`text-xs font-semibold ${isBalanced ? 'text-green-700' : 'text-amber-700'}`}>
          {totalPallets} / {requiredPallets} pallets placed
        </span>
      </div>
    </div>
  );
}
